import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { debounceTime, interval, Subject, takeUntil, takeWhile } from "rxjs";
import { FormFieldsType } from "../../modals/models/form-actions.model";
import { FiltersBar, FilterData } from "./models/filters-bar.model";
import { StickinessService } from "src/app/core/services/stickiness.service";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: "app-filters-bar",
  templateUrl: "./filters-bar.component.html",
  styleUrls: ["./filters-bar.component.scss"],
})
export class FiltersBarComponent implements OnInit {
  @Input() filters: any = [];
  @Input() cssColContainer: string = "";
  @Output() filterSelected: EventEmitter<any> = new EventEmitter();
  @Output() filterState: EventEmitter<any> = new EventEmitter();
  form: FormGroup = new FormGroup({});
  inputFormControl: FormControl = new FormControl("", Validators.minLength(1));
  searchFormControl: FormControl = new FormControl("");
  filterFieldType = FormFieldsType;
  private destroy$ = new Subject<void>();
  private controlSubscriptions: Map<string, any> = new Map();

  constructor(private stickinessService: StickinessService,private route: ActivatedRoute) {}

  ngOnInit(): void {
     this.initializeForm();
    this.subscribeToFormControls();
    // Poll every 200ms until data is populated for all filters
    interval(200)
      .pipe(
        takeUntil(this.destroy$),
        takeWhile(() => !this.filtersAreReady()))
      .subscribe({
        complete: () => {
          // Get query parameters from the route
          this.route.queryParams.subscribe((params) => {
            let formValues;
        
            if (Object.keys(params).length > 0) {
              // Parse and convert query parameters based on filters
              formValues = this.parseQueryParams(params);
            } else {
              // Fallback to stickiness service
              formValues = this.stickinessService.restoreFilterState();
            }
        
            if (formValues) {
              this.form.patchValue(formValues);
              this.filterState.emit(this.transformFormValue(formValues));
              this.applyFormValuesToFilters(formValues);
            } else {
              this.setDefaultSelectionForFilters();
              this.filterState.emit(null);
            }
             this.setDefaultValues();
          });
        },
      });
  }       

  private parseQueryParams(params: any): any {
    const parsedValues: any = {};
  
    Object.keys(params).forEach((key) => {
      const filter = this.filters.find((f) => f.name === key);
  
      if (filter) {
        const paramValue = params[key];
  
        if (filter.fieldType === FormFieldsType.Multi_Select) {
          // Convert comma-separated values into an array for multi-select filters
          parsedValues[key] = paramValue.split(',').map((value: string) => {
            return filter.data.find((item: any) => item.data === value) || { data: value };
          });
        } else if (filter.fieldType === FormFieldsType.SELECT) {
          // Find the matching item for single-select filters
          parsedValues[key] = filter.data.find((item: any) => item.data === paramValue) || { data: paramValue };
        } else if (filter.fieldType === FormFieldsType.INPUT) {
          // Directly assign the value for input filters
          parsedValues[key] = paramValue;
        }
      }
    });
  
    return parsedValues;
  }

  onResetFilters(): void {
    this.form.reset();
    this.filters.forEach((filter) => {
      if (filter?.data?.length > 0) {
        filter.data.forEach((item) => {
          item.isSelected = false; // Set isSelected to false for all items
        });
      }
    });
    this.stickinessService.saveFilterState(this.form.value);
     this.setDefaultSelectionForFilters();
     this.setDefaultValues();
     this.filterState.emit(this.transformFormValue(this.form.value));
  }

  matchesSearch(datum: any, searchText: string): boolean {
    if (!searchText) return true;
    return datum.name?.toLowerCase().includes(searchText.toLowerCase());
  }

  isUserSelectedAndNotFirst(filterName: string): boolean {
    const control = this.form.get(filterName);
    const filter = this.filters.find(f => f.name === filterName);
    const firstOption = filter?.data?.[0]; // Get the first option in the filter data
  
    if (!control?.value) {
      return false; // No value selected
    }
  
    if (Array.isArray(control.value)) {
      // Multi-select: Check if any selected value is not the first option
      return control.value.some(value => value !== firstOption);
    }
  
    // Single-select: Check if the selected value is not the first option
    return control.value !== firstOption;
  }

  private applyFormValuesToFilters(formValues: any): void {
    this.filters.forEach((filter) => {
      filter.data?.forEach((item) => {
        if (Array.isArray(formValues[filter.name])) {
          // Handle multi-select filters
          item.isSelected = formValues[filter.name].some(
            (formItem) => formItem.data === item.data
          );
        } else {
          // Handle single-select filters
          item.isSelected = Array.isArray(formValues[filter.name])
          ? formValues[filter.name].some((formItem) => {
              if (typeof item.data === 'object' && item.data !== null && typeof formItem.data === 'object' && formItem.data !== null) {
                return item.data.id === formItem.data.id;
              } else {
                return item.data === formItem.data;
              }
            })
          : (typeof item.data === 'object' && item.data !== null && typeof formValues[filter.name]?.data === 'object' && formValues[filter.name]?.data !== null)
            ? item.data.id === formValues[filter.name]?.data?.id
            : item.data === formValues[filter.name]?.data;
        }
      });
      // Ensure at least one item is selected if no item is selected
      if (!filter.data?.some((item) => item.isSelected)) {
        if (filter.data && filter.data[0].type !== Can_Be_Empty) {
          filter.data[0].isSelected = true; // Default to the first item
        }
      }
    });
  }
  
  private setDefaultSelectionForFilters(): void {
    this.filters.forEach((filter) => {
      if (filter?.data?.length > 0) {
        filter.data[0].isSelected = true; // Default to the first item
      }
    });
  }

  private filtersAreReady(): boolean {
    return this.filters.every((filter) => {
      if (filter.fieldType === FormFieldsType.INPUT) {
        // Input filters do not have a `data` array, so consider them ready
        return true;
      }
      // For other filters, ensure `data` is an array and has at least one item
      return Array.isArray(filter.data) && filter.data.length > 0;
    });
  }

  private initializeForm(): void {
    const formControls = {};
    this.filters.forEach((filter) => {
      if (filter.fieldType === FormFieldsType.Multi_Select) {
        formControls[filter.name] = new FormControl([]);
      } else if (filter.fieldType === FormFieldsType.SELECT) {
        formControls[filter.name] = new FormControl(null);
      } else if (filter.fieldType === FormFieldsType.INPUT) {
        formControls[filter.name] = new FormControl(null);
      }
    });
    this.form = new FormGroup(formControls);
  }

  private setDefaultValues(): void {
    // Unsubscribe from valueChanges to prevent emissions
    this.controlSubscriptions.forEach((subscription) => subscription.unsubscribe());
  
    this.filters.forEach((filter) => {
      const selectedValues = filter.data?.filter((item) => item.isSelected);
      if (
        filter.fieldType === FormFieldsType.SELECT &&
        selectedValues?.length
      ) {
        this.form.controls[filter.name].setValue(selectedValues[0], { emitEvent: false });
      } else if (
        filter.fieldType === FormFieldsType.Multi_Select &&
        selectedValues?.length
      ) {
        this.form.controls[filter.name].setValue(selectedValues, { emitEvent: false });
      }
    });
  
    // Resubscribe to valueChanges after setting default values
    this.subscribeToFormControls();
  }

  private subscribeToFormControls(): void {
    this.filters.forEach((filter) => {
      if (filter.fieldType === this.filterFieldType.INPUT) {
        const control = this.form.get(filter.name);
        if (control) {
          const subscription = control.valueChanges
            .pipe(
              takeUntil(this.destroy$),
              debounceTime(1000) // Add debounce to avoid frequent calls
            )
            .subscribe((value) => {
  
              let filterData: FilterData<string, string> = {
                id: 1,
                name: filter.name,
                type: filter.fieldType.toString(),
                data: value,
                filterName: filter.name,
              };
              this.stickinessService.saveFilterState(this.form.value);
              this.filterSelected.emit([filterData]);
            });
          // Store the subscription for later management
          this.controlSubscriptions.set(filter.name, subscription);
        }
      }
    });
  
    this.searchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(400)
      )
      .subscribe((value) => {
        this.filterSelected.emit([value]);
      });

      this.form.get('Organization')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setTenantToAll();
        this.setVenueToAll(); // Also reset venues
      });
    
    this.form.get('Tenant')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setVenueToAll();
      });
  }

  setTenantToAll(): void {
    const tenantFilter = this.filters.find((f) => f.name === "Tenant");
    if (!tenantFilter?.data?.length) return;

    this.form.controls["Tenant"]?.setValue(null, { emitEvent: false });

    tenantFilter.data.forEach((item, index) => {
      item.isSelected = index === 0;
    });

    this.form.controls["Tenant"]?.setValue(tenantFilter.data[0], {
      emitEvent: false,
    });
  }

  setVenueToAll(): void {
    const venueFilter = this.filters.find((f) => f.name === "Venue");
    if (!venueFilter?.data?.length) return;

    this.form.controls["Venue"]?.setValue(null, { emitEvent: false });

    venueFilter.data.forEach((item, index) => {
      item.isSelected = index === 0;
    });

    this.form.controls["Venue"]?.setValue(venueFilter.data[0], {
      emitEvent: false,
    });
  } 

  private transformFormValue(formValue: any): Record<string, any> {
    const transformed: Record<string, any> = {};

    Object.keys(formValue).forEach((key) => {
      const value = formValue[key];

      if (Array.isArray(value)) {
        // For multi-select filters, filter out null or undefined values
        transformed[key] = value
          .map((item) => item?.data)
          .filter((data) => data !== null && data !== undefined);
      } else if (value && typeof value === "object") {
        // For single-select filters, ensure value is not null
        transformed[key] = value.data ?? null;
      } else {
        // For other cases (e.g., input fields)
        transformed[key] = value ?? null;
      }
    });
    return transformed;
  }

  onSingleSelectionChanged(filterName: string, value: any): void {
    this.stickinessService.saveFilterState(this.form.value);
    this.emitSingleSelection(filterName, value);
  }

  onMultiSelectionChanged(filterName: string, values: any[]): void {
    if (!values.length) {
      const filter = this.filters.find((f) => f.name === filterName);

      if (filter.data[0].filterName !== Can_Be_Empty) {
        const fakeItem = filter?.data?.find((item) => item.isFakeItem);
        if (fakeItem) {
          values = [fakeItem]; // Set the fake item as the value
        }
      }
    }
    const isLastItemFake =
      values.length > 0 && values[values.length - 1].isFakeItem;
    if (isLastItemFake) {
      // Keep only the fake item in the values array
      values = values.filter((item) => item.isFakeItem);
    } else {
      // Remove any fake items if they are not the last item
      values = values.filter((item) => !item.isFakeItem);
    }
    this.form.controls[filterName].setValue(values);
    this.emitMultiSelection(filterName, values);
  }

  private emitSingleSelection(filterName: string, value: any): void {
    this.filterSelected.emit([value]);
  }

  private emitMultiSelection(filterName: string, data: any[]): void {
    const validData = data
      .filter((item) => item && item.data !== null && item.data !== undefined); // Filter out invalid items
  
    const res = {
      filterName,
      data: validData.length > 0 ? validData.map((item) => item.data) : [], // Map valid items or set to null
    };
    this.stickinessService.saveFilterState(this.form.value);
    this.filterSelected.emit([res]);
  }

  getFilteredData(filter: any): any[] {
    const rawValue = this.searchFormControl.value;
    const searchValue =
      typeof rawValue === "string" ? rawValue.toLowerCase() : "";

    const filteredData =
      filter.searchable && searchValue
        ? filter.data.filter((item) =>
            item.name?.toLowerCase().includes(searchValue)
          )
        : filter.data;

    return filteredData;
  }

  viewHandle = (item: any): string => item?.name ?? item;

  getTriggerValue(filter: FiltersBar<any, any>): string {
    const value = this.form.controls[filter.name]?.value;
    return Array.isArray(value) && value.length > 0
      ? value[0]?.name
      : value?.name || "";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete(); // Clean up all subscriptions
  }
}

export const Can_Be_Empty = "Can_Be_Empty";